import { createSelector } from '@reduxjs/toolkit'
import { buildSimilarityIndex } from '@safe-global/utils/utils/addressSimilarity'
import type { SimilarityIndex } from '@safe-global/utils/utils/addressSimilarity.types'
import { selectAllAddressBooks } from '@/store/addressBookSlice'
import { selectAllAddedSafes } from '@/store/addedSafesSlice'
import { selectAllCuratedNestedSafes } from '@/store/settingsSlice'
import { selectUndeployedSafes } from '@/features/counterfactual/store'
import type { RootState } from '@/store'

/**
 * Every address the user has EXPLICITLY trusted, gathered only from local sources
 * that an attacker cannot inject via fetched/chain data:
 *  - local address book contacts
 *  - added / pinned safes
 *  - curated nested safes
 *  - undeployed (counterfactual) safes the user authored
 *
 * Flattened across chains, lowercased, deduped. Deliberately excludes CGW
 * owner-owned safes and viewed-Safe owners — those are poisonable candidates,
 * never anchors, so the similarity baseline itself cannot be poisoned.
 */
export const selectAnchorAddresses = createSelector(
  [
    selectAllAddressBooks,
    selectAllAddedSafes,
    // `selectAllCuratedNestedSafes` lives near the end of settingsSlice, which is
    // still mid-evaluation when this module is first pulled in via the store barrel
    // (slices.ts). Reading the binding eagerly here yields `undefined` and crashes
    // createSelector. Wrap it so the live binding is resolved at call-time instead.
    (state: RootState) => selectAllCuratedNestedSafes(state),
    selectUndeployedSafes,
  ],
  (addressBooks, addedSafes, curatedNested, undeployedSafes): string[] => {
    const addresses: string[] = []
    for (const perChain of Object.values(addressBooks)) addresses.push(...Object.keys(perChain))
    for (const perChain of Object.values(addedSafes)) addresses.push(...Object.keys(perChain))
    addresses.push(...curatedNested)
    for (const perChain of Object.values(undeployedSafes)) addresses.push(...Object.keys(perChain))
    // Lowercase + dedupe. The sources already store lowercased addresses and buildSimilarityIndex
    // re-normalizes anyway, so this is belt-and-braces to keep the anchor set canonical.
    return Array.from(new Set(addresses.map((address) => address.toLowerCase())))
  },
)

/**
 * Memoized similarity index over the user's trusted anchors. Rebuilt only when
 * the anchor set changes (reselect). The index (Maps + closures) lives ONLY in
 * this selector's output — never in store state or a dispatched action — so it
 * is never poisoned by fetched data and never trips `serializableCheck`.
 */
export const selectAnchorIndex = createSelector(
  [selectAnchorAddresses],
  (anchors): SimilarityIndex => buildSimilarityIndex(anchors),
)
